import i18n from "i18next";

/**
 * The owner's own wording (Control Center → Wording): { ar: { "nav.cashier": "..." }, en: {...} } laid over the built-in texts.
 * What was there before is remembered, so removing an override brings the original back without a reload.
 */
export type Labels = { ar?: Record<string, string>; en?: Record<string, string> };
const originals = new Map<string, Map<string, unknown>>();

function setKey(lng: string, key: string, value: unknown) {
  const nested: Record<string, unknown> = {};
  let cur = nested;
  const parts = key.split(".");
  parts.slice(0, -1).forEach((p) => { cur[p] = {}; cur = cur[p] as Record<string, unknown>; });
  cur[parts[parts.length - 1]!] = value;
  i18n.addResourceBundle(lng, "translation", nested, true, true);
}

export function applyLabels(labels: Labels | undefined | null): void {
  for (const lng of ["ar", "en"] as const) {
    const wanted = labels?.[lng] ?? {};
    const mem = originals.get(lng) ?? new Map<string, unknown>();
    originals.set(lng, mem);
    for (const key of [...mem.keys()]) {
      if (!(key in wanted)) { setKey(lng, key, mem.get(key)); mem.delete(key); } // override removed: the original text returns
    }
    for (const [key, value] of Object.entries(wanted)) {
      if (!mem.has(key)) mem.set(key, i18n.getResource(lng, "translation", key));
      setKey(lng, key, value);
    }
  }
  void i18n.changeLanguage(i18n.language); // re-render everything in the current language
}

/** The built-in wording of a key (ignoring any override), or the current one when it was never overridden. */
export function builtinText(lng: "ar" | "en", key: string): string {
  const mem = originals.get(lng);
  const v = mem?.has(key) ? mem.get(key) : i18n.getResource(lng, "translation", key);
  return typeof v === "string" ? v : "";
}

/** Every screen text as "dotted.key" -> string, for the Wording editor. */
export function allTexts(lng: "ar" | "en"): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const walk = (o: unknown, prefix: string) => {
    if (typeof o === "string") out.push([prefix, builtinText(lng, prefix)]);
    else if (o && typeof o === "object") for (const [k, v] of Object.entries(o)) walk(v, prefix ? `${prefix}.${k}` : k);
  };
  walk(i18n.getResourceBundle(lng, "translation"), "");
  return out;
}
